// @ts-nocheck
import { Box, Button, Card, Code, Divider, Group, Stack, Text, Title } from '@/components/ui';
import {
  LiveRegionProvider,
  ScreenReaderOnly,
  SkipLink,
  useLiveRegion,
} from '@/components/accessibility';

// Inline demo child: useLiveRegion() must be called inside <LiveRegionProvider>.
function LiveRegionDemo() {
  const { announce } = useLiveRegion();
  const messages = [
    'Item added to cart',
    'Settings saved successfully',
    'New notification received',
  ];

  return (
    <Group>
      {messages.map((message) => (
        <Button
          key={message}
          variant="light"
          onClick={() => announce(message)}
        >
          Announce: "{message}"
        </Button>
      ))}
    </Group>
  );
}

export default function AccessibilityComponentsPage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Accessibility Components (ScreenReaderOnly, SkipLink, LiveRegion)</Title>
        <Text c="dimmed" size="sm">
          Utilities for keyboard-first navigation and screen reader support - visually hidden
          content, skip-to-content links, and polite aria-live announcements
        </Text>
      </Stack>

      {/* ScreenReaderOnly Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ScreenReaderOnly</Title>
            <Text size="sm" c="dimmed">
              Wraps content in a visually hidden element so it is exposed to screen readers but not
              shown on screen. Useful for adding extra context to icon-only controls.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Example:</Text>
            <Text size="sm">
              The text after the colon is hidden visually but read aloud by assistive technology:
              <ScreenReaderOnly>
                This sentence is only available to screen readers.
              </ScreenReaderOnly>
              {' '}(nothing extra appears here visually).
            </Text>
            <Group>
              <Button variant="filled">
                Save
                <ScreenReaderOnly> the current draft document</ScreenReaderOnly>
              </Button>
              <Text size="xs" c="dimmed">
                The button announces "Save the current draft document" to screen readers.
              </Text>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Text size="sm">
  The text after the colon is hidden visually but read aloud:
  <ScreenReaderOnly>
    This sentence is only available to screen readers.
  </ScreenReaderOnly>
</Text>

<Button>
  Save
  <ScreenReaderOnly> the current draft document</ScreenReaderOnly>
</Button>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • children - ReactNode rendered inside a visually hidden wrapper
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* SkipLink Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>SkipLink</Title>
            <Text size="sm" c="dimmed">
              A "skip to content" link that stays off-screen until it receives keyboard focus, then
              slides into view. Lets keyboard users bypass repeated navigation.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Example:</Text>
            <Text size="xs" c="dimmed">
              The link below is hidden until focused. Click inside this card and press Tab to reveal
              it near the top-left of the page.
            </Text>
            {/* Relative box so the absolutely-positioned link anchors to this area */}
            <Box style={{ position: 'relative', minHeight: 56 }}>
              <SkipLink href="#main" label="Skip to content" />
              <SkipLink href="#main-nav" label="Skip to navigation" />
            </Box>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`{/* Place near the top of your layout, before the main content */}
<SkipLink href="#main" label="Skip to content" />
<SkipLink href="#main-nav" label="Skip to navigation" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • label - visible link text (default: "Skip to content")
              <br />
              • href - anchor target id to jump to (default: "#main")
              <br />
              • ...rest - standard anchor attributes are forwarded
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* LiveRegion Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>LiveRegionProvider / useLiveRegion</Title>
            <Text size="sm" c="dimmed">
              Provides a polite aria-live region plus an announce() function. Mount the provider once
              high in the tree, then call useLiveRegion() from any descendant to announce status
              updates to screen readers.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Live Example:</Text>
            <Text size="xs" c="dimmed">
              Each button calls announce(...). The message is written to a hidden aria-live region
              and read aloud by screen readers (no visible change on screen).
            </Text>
            <LiveRegionProvider>
              <LiveRegionDemo />
            </LiveRegionProvider>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`function Toolbar() {
  const { announce } = useLiveRegion();
  return (
    <Button onClick={() => announce('Settings saved successfully')}>
      Save settings
    </Button>
  );
}

// Mount the provider once, high in your app tree:
<LiveRegionProvider>
  <Toolbar />
</LiveRegionProvider>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key API:</Text>
            <Text size="xs" c="dimmed" component="div">
              • LiveRegionProvider - wraps children and renders the hidden aria-live region
              <br />
              • useLiveRegion() - returns {`{ announce(message: string) }`}; must be used inside the
              provider
            </Text>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      <Text size="xs" c="dimmed">
        These components are presentation-only helpers with no network, SDK, or auth dependencies.
      </Text>
    </Stack>
  );
}
